// ---------------------------------------------------------------------------
// YouAreYou (uru) — Connection Request Page
//
// Public page rendered at /v/:code (global link), /c/:uuid (direct link),
// and /connect/verify?token= (user returning from SMS verification).
//
// Flow for unauthenticated recipients:
//
//   "show_requestor"
//     Resolves the link via GET /api/uru/connect/resolve/{code}.
//     Shows requestor info with real verification badges from requestor_verifications.
//     Collects recipient's phone number.
//     POST /api/uru/connect/start {phone, connection_code|connection_uuid}
//
//   "verify_waiting"
//     Static: "Check your phone for a verification link."
//     On mount with ?token=: calls connectVerify immediately.
//
//   "verifying"
//     Transient: calling POST /api/uru/connect/verify {token}
//     Dispatches on next_step:
//       "already_verified"   → "accept_deny" state
//       "needs_registration" → navigate(/register, {state: {connection_uuid}})
//       "needs_kyc"          → navigate(/register, {state: {connection_uuid}})
//
//   "accept_deny"
//     Shows Accept / Decline buttons.
//     Looks up connection PK via GET /api/uru/connections?search={connection_uuid}
//     Calls acceptConnection(pk) or denyConnection(pk).
//
// For authenticated recipients:
//   Skip phone verification — show Accept/Deny directly.
//   Resolve the connection PK from the connections list.
//
// API references:
//   GET  /api/uru/connect/resolve/{identifier}
//   POST /api/uru/connect/start
//   POST /api/uru/connect/verify
//   GET  /api/uru/connections?search={uuid}
//   POST /api/uru/connections/{pk} {accept: true} | {deny: {...}}
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  resolveConnectionLink,
  connectStart,
  connectVerify,
  connectAccept,
  connectDeny,
  denyConnection,
  normalizePhone,
  listConnections,
  getConnection,
  grantDataSharing,
  getUruMe,
} from "@/api/endpoints";
import type { ResolveConnectionData, RequestorVerifications, SharableField } from "@/types/api";
import { resolveAvatarUrl } from "@/types/api";
import { useAuth } from "@/hooks/useAuth";
import Icon from "@/components/ui/Icon";
import BrandName from "@/components/ui/BrandName";
import BrandMark from "@/components/ui/BrandMark";
import LogoSpinner from "@/components/ui/LogoSpinner";

// ---------------------------------------------------------------------------
// Badge config — maps API verification flags to display labels/icons
// ---------------------------------------------------------------------------

interface BadgeConfig {
  key: keyof RequestorVerifications;
  icon: string;
  label: string;
}

const BADGE_CONFIGS: BadgeConfig[] = [
  { key: "is_gov_id_verified", icon: "badge", label: "Government ID" },
  { key: "is_liveness_verified", icon: "face", label: "Liveness Scan" },
  { key: "is_phone_verified", icon: "smartphone", label: "Phone Number" },
  { key: "is_email_verified", icon: "mail", label: "Email Address" },
  { key: "is_ssn_verified", icon: "fingerprint", label: "SSN Verified" },
  { key: "is_address_verified", icon: "home", label: "Address" },
  { key: "is_ofac_verified", icon: "gpp_good", label: "OFAC Cleared" },
  { key: "is_adverse_media_verified", icon: "newspaper", label: "Adverse Media" },
];

// ---------------------------------------------------------------------------
// Required field config — maps required_fields array values to display info
// ---------------------------------------------------------------------------

interface RequiredFieldConfig {
  label: string;
  icon: string;
}

const REQUIRED_FIELD_CONFIGS: Record<string, RequiredFieldConfig> = {
  gov_id:         { label: "Government ID",  icon: "badge" },
  liveness:       { label: "Liveness Scan",  icon: "face" },
  phone:          { label: "Phone Number",   icon: "smartphone" },
  email:          { label: "Email Address",  icon: "mail" },
  ssn:            { label: "SSN",            icon: "fingerprint" },
  address:        { label: "Address",        icon: "home" },
  ofac:           { label: "OFAC Check",     icon: "gpp_good" },
  adverse_media:  { label: "Adverse Media",  icon: "newspaper" },
  signature:      { label: "Signature",      icon: "draw" },
  dl_back:        { label: "Driver's License (back)", icon: "id_card" },
};

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type PageState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "show_requestor"; data: ResolveConnectionData }
  | { kind: "phone_form"; data: ResolveConnectionData }
  | { kind: "phone_submitting"; data: ResolveConnectionData }
  | { kind: "verify_waiting"; data: ResolveConnectionData }
  | { kind: "verifying"; data: ResolveConnectionData }
  | { kind: "actioning"; data: ResolveConnectionData }
  | { kind: "result"; action: "accepted" | "denied"; requesterName: string; connectionUuid?: string; connectionPk?: number };

// ---------------------------------------------------------------------------
// Shared layout components
// ---------------------------------------------------------------------------

function PageHeader() {
  const { status, user, logout } = useAuth();
  const isAuthenticated = status === "authenticated";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <header className="border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark className="h-8 w-8" />
          <span className="text-lg font-bold"><BrandName /></span>
        </Link>

        {isAuthenticated ? (
          /* Authenticated: show app nav context */
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="hidden items-center gap-1.5 text-sm font-medium text-text-secondary transition-colors hover:text-text-primary md:flex"
            >
              <Icon name="dashboard" className="text-base" />
              Dashboard
            </Link>
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-text-primary shadow-sm transition-colors hover:bg-gray-50"
                aria-label="My Profile"
                aria-expanded={menuOpen}
              >
                <Icon name="account_circle" className="text-xl text-primary-500" />
                <span className="hidden sm:inline">
                  {user?.first_name || "My Profile"}
                </span>
                <Icon name="expand_more" className="text-base text-text-muted" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <div className="border-b border-gray-100 px-4 py-2">
                    <p className="text-xs font-semibold text-text-primary truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                  </div>
                  <Link
                    to="/dashboard"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-gray-50 hover:text-text-primary"
                  >
                    <Icon name="dashboard" className="text-base" />
                    Dashboard
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-gray-50 hover:text-text-primary"
                  >
                    <Icon name="settings" className="text-base" />
                    Settings
                  </Link>
                  <button
                    onClick={() => { setMenuOpen(false); void logout(); }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-error transition-colors hover:bg-gray-50"
                  >
                    <Icon name="logout" className="text-base" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Unauthenticated: marketing nav */
          <nav className="flex items-center gap-4">
            <div className="hidden items-center gap-6 md:flex">
              <Link to="/how-it-works" className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary">
                How it works
              </Link>
              <Link to="/security" className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary">
                Security
              </Link>
              <Link to="/support" className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary">
                Help
              </Link>
            </div>
            <Link to="/login" className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600">
              Sign in
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}

function PageFooter() {
  return (
    <footer className="mt-auto border-t border-gray-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <p className="text-center text-sm text-text-secondary">
          Secure, Encrypted Identity Verification by{" "}
          <BrandName />
        </p>
        <div className="mt-3 flex items-center justify-center gap-4 text-xs text-text-muted">
          <Link to="/privacy" className="transition-colors hover:text-text-secondary">Privacy Policy</Link>
          <span>·</span>
          <Link to="/terms" className="transition-colors hover:text-text-secondary">Terms of Service</Link>
          <span>·</span>
          <Link to="/support" className="transition-colors hover:text-text-secondary">Contact Support</Link>
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Requestor panel — real verification badges from API
// ---------------------------------------------------------------------------

interface RequesterPanelProps {
  name: string;
  avatarUrl: string | null;
  verifications: RequestorVerifications;
}

function RequesterPanel({ name, avatarUrl, verifications }: RequesterPanelProps) {
  const [imgError, setImgError] = useState(false);

  const initials = name
    .split(" ")
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

  const verifiedBadges = BADGE_CONFIGS.filter((b) => verifications[b.key] === true);

  return (
    <div className="flex flex-col items-center p-8 text-center md:p-12">
      <div className="relative">
        <div className="h-28 w-28 overflow-hidden rounded-full border-4 border-primary-200 p-1 sm:h-32 sm:w-32">
          {avatarUrl && !imgError ? (
            <img
              src={avatarUrl}
              alt={`${name}'s profile photo`}
              className="h-full w-full rounded-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-full bg-primary-100 text-2xl font-bold text-primary-600 sm:text-3xl">
              {initials || "?"}
            </div>
          )}
        </div>
        <div className="absolute bottom-1 right-1 rounded-full bg-white p-0.5">
          <Icon name="check_circle" filled className="text-2xl text-primary-500" label={`${name} is verified`} />
        </div>
      </div>

      <div className="mt-4">
        <h3 className="text-xl font-bold text-text-primary">{name}</h3>
        <p className="flex items-center justify-center gap-1 text-sm font-semibold text-primary-500">
          <Icon name="verified" filled className="text-base text-primary-500" />
          <BrandName /> Verified
        </p>
        <p className="mt-1 text-xs font-bold uppercase tracking-widest text-text-muted">Requester</p>
        {verifications.kyc_level >= 2 && (
          <span className="mt-2 inline-block rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-bold uppercase text-primary-600">
            Verification Level {verifications.kyc_level}
          </span>
        )}
      </div>

      <div className="mt-6 w-full space-y-3">
        {verifiedBadges.map((badge) => (
          <div
            key={badge.key}
            className="flex items-center justify-between rounded-xl bg-[#e9edfe] p-3.5"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white">
                <Icon name={badge.icon} className="text-xl text-[#974446]" />
              </div>
              <span className="text-sm font-semibold text-[#161b27]">{badge.label}</span>
            </div>
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#e8f3ec]">
              <Icon name="check" filled className="text-sm text-[#3f7d54]" />
            </div>
          </div>
        ))}
        {verifiedBadges.length === 0 && (
          <p className="text-xs text-text-muted">Verification details unavailable.</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="flex min-h-dvh flex-col bg-background-light">
      <PageHeader />
      <main className="flex flex-1 flex-col items-center px-4 py-8 sm:px-6 sm:py-12">
        <div className="mb-8 w-full max-w-2xl text-center">
          <div className="mx-auto h-10 w-3/4 animate-pulse rounded-lg bg-gray-200 sm:h-14" />
          <div className="mx-auto mt-4 h-5 w-1/2 animate-pulse rounded-lg bg-gray-200" />
        </div>
        <div className="w-full max-w-5xl overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl shadow-gray-200/50">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="flex flex-col items-center p-8 md:p-12">
              <div className="h-32 w-32 animate-pulse rounded-full bg-gray-200" />
              <div className="mt-4 h-6 w-36 animate-pulse rounded bg-gray-200" />
              <div className="mt-2 h-4 w-24 animate-pulse rounded bg-gray-200" />
              <div className="mt-10 w-full space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-14 w-full animate-pulse rounded-xl bg-gray-100" />
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center bg-gray-50/50 p-8 md:p-12">
              <div className="h-32 w-32 animate-pulse rounded-full bg-gray-200" />
              <div className="mt-4 h-6 w-32 animate-pulse rounded bg-gray-200" />
              <div className="mt-8 h-14 w-full animate-pulse rounded-2xl bg-gray-200" />
            </div>
          </div>
        </div>
      </main>
      <PageFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorState({ code, message, onRetry }: { code: string; message: string; onRetry: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col bg-background-light">
      <PageHeader />
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-error/10">
            <Icon name="link_off" className="text-4xl text-error" label="Invalid link" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary">Link Not Found</h1>
          <p className="mt-3 text-sm text-text-secondary">{message}</p>
          <div className="mt-4 inline-block rounded-lg bg-gray-100 px-3 py-1.5 font-mono text-xs text-text-muted">
            {code}
          </div>
          <div className="mt-8 flex flex-col items-center gap-3">
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <Icon name="refresh" className="text-lg" />
              Try Again
            </button>
            <Link to="/" className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary">
              Go to Homepage
            </Link>
          </div>
          <div className="mt-8 rounded-xl border border-gray-200 bg-white p-4 text-left">
            <p className="text-xs font-semibold text-text-primary">This might happen if:</p>
            <ul className="mt-2 space-y-1.5 text-xs text-text-secondary">
              <li className="flex items-start gap-2"><Icon name="schedule" className="mt-0.5 text-sm text-text-muted" />The link has expired</li>
              <li className="flex items-start gap-2"><Icon name="link" className="mt-0.5 text-sm text-text-muted" />The URL was copied incorrectly</li>
              <li className="flex items-start gap-2"><Icon name="check_circle" className="mt-0.5 text-sm text-text-muted" />This was a single-use link that&apos;s already been used</li>
              <li className="flex items-start gap-2"><Icon name="block" className="mt-0.5 text-sm text-text-muted" />The connection was cancelled by the requester</li>
            </ul>
          </div>
        </div>
      </main>
      <PageFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action result (accepted / denied)
// ---------------------------------------------------------------------------

function ActionResult({ action, requesterName, connectionPk }: { action: "accepted" | "denied"; requesterName: string; connectionUuid?: string; connectionPk?: number }) {
  const isAccepted = action === "accepted";
  const firstName = requesterName.split(" ")[0] || requesterName;
  return (
    <div className="flex min-h-dvh flex-col bg-background-light">
      <PageHeader />
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-md text-center">
          <div className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full ${isAccepted ? "bg-green-50" : "bg-gray-100"}`}>
            <Icon
              name={isAccepted ? "check_circle" : "do_not_disturb_on"}
              filled
              className={`text-5xl ${isAccepted ? "text-verified" : "text-text-muted"}`}
              label={isAccepted ? "Connection accepted" : "Connection declined"}
            />
          </div>
          <h1 className="text-2xl font-bold text-text-primary">
            {isAccepted ? `You're verified with ${firstName}` : "Connection Declined"}
          </h1>
          <p className="mt-3 text-sm text-text-secondary">
            {isAccepted
              ? `You and ${requesterName} have verified each other's identity. You can both move forward with confidence.`
              : `You've declined the connection request from ${requesterName}.`}
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            {isAccepted ? (
              <>
                <Link
                  to={connectionPk ? `/connections/${connectionPk}` : "/connections"}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  <Icon name="verified_user" className="text-lg" />
                  View {firstName}'s verified profile →
                </Link>
                <Link
                  to="/connections"
                  className="text-sm text-text-secondary hover:text-text-primary"
                >
                  Go to my connections
                </Link>
              </>
            ) : (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <Icon name="home" className="text-lg" />
                Go to Dashboard
              </Link>
            )}
          </div>
        </div>
      </main>
      <PageFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ConnectionRequestPage() {
  const { code, uuid } = useParams<{ code?: string; uuid?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { status: authStatus, loginWithMagicLink, user } = useAuth();
  const isAuthenticated = authStatus === "authenticated";

  const linkCode = code ?? uuid ?? "";
  const isDirectLink = Boolean(uuid);
  const verifyToken = searchParams.get("token");
  const connectionParam = searchParams.get("connection");
  // ?pv= is a pre-verified phone token embedded by the backend when sending a direct link
  // to a specific phone number. The resolve endpoint consumes it and writes the result
  // to the connection config — subsequent page loads get recipient_info without ?pv=.
  const pvToken = searchParams.get("pv");

  const [pageState, setPageState] = useState<PageState>(() =>
    linkCode
      ? { kind: "loading" }
      : verifyToken
        ? { kind: "loading" }
        : {
          kind: "error",
          message: "No connection code provided. Please check the link and try again.",
        },
  );

  const [phone, setPhone] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const [mobileTab, setMobileTab] = useState<"prove" | "requester">("prove");

  // Prevent double-fire of verify effect
  const verifyCalledRef = useRef(false);
  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => { isAuthenticatedRef.current = isAuthenticated; }, [isAuthenticated]);

  // ---- Fetch connection data (resolve) ----
  // Passes ?pv= to the backend on first load so it gets consumed and written to the connection.
  useEffect(() => {
    if (!linkCode) return;
    let cancelled = false;

    async function load() {
      // For authenticated users, check whether this is their own link BEFORE
      // calling resolve. This handles the case where the link is already used/expired
      // (resolve returns 404) but the owner clicks it again — we redirect them
      // rather than showing a dead-link error.
      if (isAuthenticated) {
        try {
          if (!isDirectLink) {
            // Global link (/v/:code): linkCode IS the connection_code.
            const me = await getUruMe();
            if (cancelled) return;
            if (me.connection_code === linkCode) {
              navigate("/dashboard", { replace: true });
              return;
            }
          } else {
            // Direct link (/c/:uuid): find an outgoing connection with this UUID.
            const conns = await listConnections({ direction: "outgoing" });
            if (cancelled) return;
            const match = conns.find((c) => c.uuid === linkCode);
            if (match) {
              navigate(`/connections?openUuid=${encodeURIComponent(linkCode)}`, { replace: true });
              return;
            }
          }
        } catch {
          // Pre-check failed — fall through to resolve as normal.
        }
      }

      resolveConnectionLink(linkCode, pvToken || undefined).then(
        (data) => {
          if (cancelled) return;
          // Strip ?pv= from the URL so it's not re-sent on refresh (already consumed by backend)
          if (pvToken) {
            const next = new URLSearchParams(searchParams);
            next.delete("pv");
            setSearchParams(next, { replace: true });
          }
          setPageState({ kind: "show_requestor", data });
        },
        (err: unknown) => {
          if (cancelled) return;
          let message = "This connection link could not be found. It may have expired or been cancelled.";
          if (err instanceof Error) {
            if (err.message.includes("404") || err.message.includes("not found")) {
              message = "This connection link doesn't exist or has expired. Please ask the sender for a new link.";
            } else if (err.message.includes("network") || err.message.includes("Network")) {
              message = "Unable to reach the server. Please check your internet connection and try again.";
            }
          }
          setPageState({ kind: "error", message });
        },
      );
    }

    void load();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkCode, fetchAttempt]);

  // ---- Direct /connect/verify?token= entry (no linkCode in URL) ----
  // When the user clicks the SMS verification link they land here with no connection
  // code in the path. We restore context from sessionStorage and call connectVerify.
  useEffect(() => {
    if (!verifyToken || linkCode || verifyCalledRef.current) return;
    verifyCalledRef.current = true;

    let storedCode = "";
    let storedIsDirect = false;
    let storedLastName: string | undefined;
    let storedRequestorName: string | undefined;
    let storedRequestorFaceUrl: string | null | undefined;
    const stored = sessionStorage.getItem("uru_connect_ctx");
    if (stored) {
      try {
        const ctx = JSON.parse(stored) as { linkCode: string; isDirectLink: boolean; lastName?: string; requestorName?: string; requestorFaceUrl?: string | null };
        storedCode = ctx.linkCode;
        storedIsDirect = ctx.isDirectLink;
        storedLastName = ctx.lastName;
        storedRequestorName = ctx.requestorName;
        storedRequestorFaceUrl = ctx.requestorFaceUrl;
      } catch { /* ignore malformed stored value */ }
    }
    sessionStorage.removeItem("uru_connect_ctx");

    connectVerify({ token: verifyToken })
      .then((res) => {
        _routeAfterVerify(res, storedCode, storedIsDirect, storedLastName, storedRequestorName, storedRequestorFaceUrl, verifyToken ?? undefined);
      })
      .catch(() => {
        // Token expired/used — check live whether the user is already authenticated.
        // This handles the case where someone registered via this link and clicks it again.
        getUruMe()
          .then(() => {
            // User is logged in — send them to the connection or connections list.
            const dest = connectionParam
              ? `/connections?openUuid=${encodeURIComponent(connectionParam)}`
              : "/connections";
            navigate(dest, { replace: true });
          })
          .catch(() => {
            setPageState({
              kind: "error",
              message: "Verification link is invalid or expired. Please go back to the original link and try again.",
            });
          });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Route after connect/verify completes ----
  // Shared by the phone-form verify path and the /connect/verify?token= entry path.
  const _routeAfterVerify = useCallback((
    res: { next_step: string; phone?: string; person_name?: string | null; has_uru_account?: boolean; ml_token?: string },
    resolvedCode: string,
    resolvedIsDirect: boolean,
    resolvedLastName?: string,
    resolvedRequestorName?: string,
    resolvedRequestorFaceUrl?: string | null,
    smsToken?: string,
  ) => {
    const connectionState = resolvedIsDirect
      ? { connection_uuid: resolvedCode || undefined }
      : { connection_code: resolvedCode || undefined };

    if (res.next_step === "already_verified" && res.has_uru_account && res.ml_token) {
      // ml_token present — log in directly then navigate to connection page (will auto-accept as authenticated)
      const redirectPath = resolvedIsDirect ? `/c/${resolvedCode}` : `/v/${resolvedCode}`;
      loginWithMagicLink(res.ml_token)
        .then(() => navigate(redirectPath || "/connections"))
        .catch(() => navigate("/login", {
          state: { from: { pathname: redirectPath }, ...connectionState },
        }));
    } else if (res.next_step === "already_verified" && res.has_uru_account) {
      // Has uru account but no ml_token — send to login
      navigate("/login", {
        state: { from: { pathname: window.location.pathname }, ...connectionState },
      });
    } else {
      // needs_registration, needs_kyc, or already_verified without uru account
      const register_step =
        res.next_step === "needs_kyc" || (res.next_step === "already_verified" && !res.has_uru_account)
          ? "dob_confirm"
          : "registration_form";

      navigate("/register", {
        state: {
          ...connectionState,
          from: window.location.pathname,
          token: smsToken,
          verified_phone: res.phone,
          register_step,
          person_name: res.person_name,
          last_name: resolvedLastName,
          requestor_name: resolvedRequestorName,
          requestor_face_url: resolvedRequestorFaceUrl,
        },
      });
    }
  }, [loginWithMagicLink, navigate]);

  // ---- Accept flow ----
  // For authenticated users: call connectAccept directly.
  // For unauthenticated users with verified phone: call connectVerify then route.
  // For unauthenticated users without verified phone: show phone form.
  const handleAcceptFlow = useCallback(async () => {
    if (pageState.kind !== "show_requestor") return;
    const { data } = pageState;

    if (isAuthenticated) {
      // Authenticated — accept directly
      setPageState({ kind: "actioning", data });
      try {
        const accepted = await connectAccept(linkCode);

        // Find our own connection record (connectAccept returns the requester's object —
        // different ID/owner — so we can't GET or grant using that ID).
        // We fetch our incoming verified connections and take the most recently created one.
        let myConnPk: number | undefined;
        try {
          const myConnections = await listConnections({ status: "verified", direction: "incoming" });
          const myConn = myConnections.sort((a, b) => b.created - a.created)[0];
          if (myConn) {
            myConnPk = myConn.id;
            // my_sharing is only on the detail endpoint, not the list
            const detail = await getConnection(myConn.id);
            const pendingEntries = Object.entries(detail.my_sharing ?? {}).filter(
              ([, entry]) => entry?.status === "pending",
            );
            console.log("[ConnectionRequestPage] auto-grant: pending entries found:", pendingEntries.map(([f]) => f));
            if (pendingEntries.length > 0) {
              // Use allSettled so a single failed grant doesn't abort the rest.
              await Promise.allSettled(
                pendingEntries.map(([field, entry]) =>
                  grantDataSharing(myConn.id, field as SharableField, entry?.mode ?? "show_valid"),
                ),
              );
            }
          } else {
            console.warn("[ConnectionRequestPage] auto-grant: no incoming verified connections found");
          }
        } catch (autoGrantErr) {
          // Best-effort — don't block the accept flow
          console.warn("[ConnectionRequestPage] auto-grant failed:", autoGrantErr);
        }

        setPageState({ kind: "result", action: "accepted", requesterName: data.requestor_name, connectionUuid: accepted.uuid, connectionPk: myConnPk });
      } catch {
        setApiError("Unable to accept. Please try again.");
        setPageState({ kind: "show_requestor", data });
      }
      return;
    }

    // Unauthenticated — check if phone is already verified via ?pv= (consumed by resolve)
    if (data.recipient_info?.phone_verified) {
      // Phone ownership already proved — use routing info from resolve, or default to registration
      _routeAfterVerify(
        {
          next_step: data.recipient_info.next_step || "needs_registration",
          phone: data.recipient_info.phone,
          person_name: data.recipient_info.person_name,
          has_uru_account: data.recipient_info.has_uru_account,
          ml_token: data.recipient_info.ml_token,
        },
        linkCode,
        isDirectLink,
        undefined,
        data.requestor_name,
        data.requestor_avatar || data.requestor_face_url,
        data.recipient_info.pv_token ?? undefined,
      );
      return;
    }

    // No verified phone — show the phone form
    setPageState({ kind: "phone_form", data });
  }, [pageState, isAuthenticated, linkCode, isDirectLink, navigate, _routeAfterVerify]);

  // ---- Decline flow ----
  // Authenticated users: look up the pending incoming connection PK and call denyConnection(pk).
  // Unauthenticated users: call the public connectDeny(code) endpoint.
  const handleDeclineFlow = useCallback(async () => {
    if (pageState.kind !== "show_requestor") return;
    const { data } = pageState;
    setPageState({ kind: "actioning", data });

    try {
      if (isAuthenticated) {
        // Find the pending incoming connection to get its PK
        const searchKey = data.connection_uuid || linkCode;
        const connections = await listConnections({ search: searchKey, status: "pending" });
        const pending = connections.find((c) => c.direction === "incoming");
        if (pending) {
          await denyConnection(pending.id);
        } else {
          // Fallback — global link deny via public endpoint
          await connectDeny(linkCode);
        }
      } else {
        await connectDeny(linkCode);
      }
      setPageState({ kind: "result", action: "denied", requesterName: data.requestor_name });
    } catch {
      setApiError("Unable to decline. Please try again.");
      setPageState({ kind: "show_requestor", data });
    }
  }, [pageState, isAuthenticated, linkCode]);

  // ---- Phone submit → connect/start (shown after Accept, when phone is unknown) ----
  const handlePhoneSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (pageState.kind !== "phone_form" && pageState.kind !== "show_requestor") return;

      const { data } = pageState;

      setApiError(null);
      const digits = phone.replace(/\D/g, "");
      if (!phone.trim() || digits.length < 7) {
        setPhoneError("Please enter a valid phone number.");
        return;
      }
      setPhoneError(null);

      setPageState({ kind: "phone_submitting", data });

      try {
        let normalizedPhone: string;
        try {
          const res = await normalizePhone({ phone_number: phone });
          normalizedPhone = res.phone_number;
        } catch {
          setPhoneError("Please enter a valid US phone number.");
          setPageState({ kind: "phone_form", data });
          return;
        }

        const payload = isDirectLink
          ? { phone: normalizedPhone, connection_uuid: linkCode, last_name: lastName || undefined }
          : { phone: normalizedPhone, connection_code: linkCode, last_name: lastName || undefined };

        await connectStart(payload);
        // Persist connection context so it survives the SMS link redirect to /connect/verify
        sessionStorage.setItem("uru_connect_ctx", JSON.stringify({
          linkCode,
          isDirectLink,
          lastName: lastName || undefined,
          requestorName: data.requestor_name,
          requestorFaceUrl: data.requestor_avatar || data.requestor_face_url || null,
        }));
        setPageState({ kind: "verify_waiting", data });
      } catch {
        setApiError("Something went wrong. Please try again.");
        setPageState({ kind: "phone_form", data });
      }
    },
    [pageState, phone, lastName, linkCode, isDirectLink],
  );

  const handleRetry = useCallback(() => {
    setPageState({ kind: "loading" });
    setFetchAttempt((n) => n + 1);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (pageState.kind === "loading") return <LoadingSkeleton />;

  if (pageState.kind === "error") {
    return (
      <ErrorState
        code={isDirectLink ? `c/${linkCode}` : `v/${linkCode}`}
        message={pageState.message}
        onRetry={handleRetry}
      />
    );
  }

  if (pageState.kind === "result") {
    return <ActionResult action={pageState.action} requesterName={pageState.requesterName} connectionUuid={pageState.connectionUuid} connectionPk={pageState.connectionPk} />;
  }

  // All remaining states have data
  const data =
    pageState.kind === "show_requestor" ? pageState.data
      : pageState.kind === "phone_form" ? pageState.data
        : pageState.kind === "phone_submitting" ? pageState.data
          : pageState.kind === "verify_waiting" ? pageState.data
            : pageState.kind === "verifying" ? pageState.data
              : pageState.kind === "actioning" ? pageState.data
                : null;

  if (!data) return <LoadingSkeleton />;

  const displayName = data.requestor_name;
  const firstName = displayName.split(" ")[0] || displayName;
  const isActioning =
    pageState.kind === "actioning" ||
    pageState.kind === "phone_submitting" ||
    pageState.kind === "verifying";

  return (
    <div className="flex min-h-dvh flex-col bg-background-light">
      <PageHeader />

      <main className="flex flex-1 flex-col items-center px-4 py-8 sm:px-6 sm:py-12">
        {/* Hero heading */}
        <div className="mb-8 w-full max-w-4xl text-center sm:mb-12">
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-text-primary sm:text-4xl md:text-5xl">
            {displayName} wants to know <BrandName />.
          </h2>
          <p className="mt-3 text-base text-text-secondary sm:mt-4 sm:text-lg">
            Mutual identity verification — trust who you're dealing with online
          </p>
        </div>

        {/* API error banner (above card) */}
        {apiError && (
          <div className="mb-4 w-full max-w-5xl">
            <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-error" role="alert">
              <Icon name="error" className="mt-0.5 shrink-0 text-lg" />
              <p>{apiError}</p>
            </div>
          </div>
        )}

        {/* Two-panel card */}
        <div className="relative w-full max-w-5xl overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl shadow-gray-200/50">
          {/* Center divider — desktop only */}
          <div className="pointer-events-none absolute bottom-10 left-1/2 top-10 hidden w-px bg-gray-200 md:block" />

          {/* Mobile tab bar — only in show_requestor state */}
          {pageState.kind === "show_requestor" && (
            <div className="flex border-b border-gray-200 md:hidden">
              <button
                onClick={() => setMobileTab("prove")}
                className={`flex-1 py-3 px-4 text-sm font-semibold transition-colors ${mobileTab === "prove" ? "border-b-2 border-primary-500 text-primary-500" : "text-text-muted"}`}
              >
                Prove You are You
              </button>
              <button
                onClick={() => setMobileTab("requester")}
                className={`flex-1 py-3 px-4 text-sm font-semibold transition-colors ${mobileTab === "requester" ? "border-b-2 border-primary-500 text-primary-500" : "text-text-muted"}`}
              >
                {displayName}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left — requestor: always on desktop, only when requester tab on mobile */}
            <div className={(pageState.kind === "show_requestor" && mobileTab !== "requester") || pageState.kind === "verify_waiting" ? "hidden md:block" : "block"}>
              <RequesterPanel
                name={displayName}
                avatarUrl={data.requestor_avatar || data.requestor_face_url}
                verifications={data.requestor_verifications}
              />
            </div>

            {/* Right — recipient actions: always on desktop, only when prove tab on mobile */}
            <div className={`flex flex-col items-center bg-gray-50/50 p-8 text-center md:p-12 ${pageState.kind === "show_requestor" && mobileTab === "requester" ? "hidden md:flex" : ""}`}>
              {/* Avatar — real if authenticated, placeholder otherwise.
                  Hidden on mobile for unauthenticated users in show_requestor:
                  the phone form replaces it there. */}
              <div className="relative">
                {isAuthenticated && user ? (
                  user.avatar ? (
                    <img
                      src={resolveAvatarUrl(user.avatar) ?? undefined}
                      alt={`${user.first_name} ${user.last_name}`}
                      className="h-28 w-28 rounded-full border-4 border-primary-200 object-cover sm:h-32 sm:w-32"
                    />
                  ) : (
                    <div className="flex h-28 w-28 items-center justify-center rounded-full border-4 border-primary-200 bg-primary-50 sm:h-32 sm:w-32">
                      <span className="text-3xl font-bold text-primary-500">
                        {(user.first_name?.[0] ?? "").toUpperCase()}{(user.last_name?.[0] ?? "").toUpperCase()}
                      </span>
                    </div>
                  )
                ) : (
                  <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-4 border-dashed border-gray-300 bg-white sm:h-32 sm:w-32">
                    <Icon name="person" className="text-7xl text-gray-200" />
                  </div>
                )}
                {!isAuthenticated && (
                  <div className="absolute bottom-1 right-1 rounded-full bg-white p-0.5">
                    <Icon name="pending" className="text-2xl text-warning" label="Verification pending" />
                  </div>
                )}
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-text-muted">
                  {isAuthenticated && user ? `${user.first_name} ${user.last_name}`.trim() : "You"}
                </h3>
              </div>

              {/* ---- Mobile: phone form shown directly for unauthenticated users ---- */}
              {pageState.kind === "show_requestor" && !isAuthenticated && (
                <div className="w-full md:hidden">
                  <p className="mb-3 text-sm text-text-secondary">
                    Enter your details to verify your identity and accept.
                  </p>
                  <form onSubmit={handlePhoneSubmit} noValidate className="space-y-2">
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Icon name="phone" className="text-lg text-text-muted" />
                      </div>
                      <input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="Phone number"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (phoneError) setPhoneError(null);
                          if (apiError) setApiError(null);
                        }}
                        aria-label="Your phone number"
                        aria-invalid={!!phoneError}
                        className={`h-11 w-full rounded-xl border py-2.5 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none ${phoneError ? "border-error" : "border-gray-300"}`}
                      />
                    </div>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Icon name="badge" className="text-lg text-text-muted" />
                      </div>
                      <input
                        type="text"
                        autoComplete="family-name"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        aria-label="Your last name"
                        className="h-11 w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                      />
                    </div>
                    {phoneError && (
                      <p className="mt-1 text-xs text-error" role="alert">{phoneError}</p>
                    )}
                    <button
                      type="submit"
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-orange-500/30 transition-all hover:from-orange-600 hover:to-orange-700"
                    >
                      Send Verification Link
                      <Icon name="arrow_forward" className="text-base" />
                    </button>
                  </form>
                </div>
              )}

              {/* ---- Accept / Decline — desktop only (mobile version is below the tab container) ---- */}
              {pageState.kind === "show_requestor" && (
                <div className="mt-6 hidden w-full flex-col gap-3 md:flex">
                  <p className="text-sm text-text-secondary">
                    You&apos;ll verify your identity to complete the connection.
                  </p>
                  <button
                    onClick={handleAcceptFlow}
                    className="flex w-full items-center justify-center gap-3 rounded-2xl bg-primary-500 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-600"
                  >
                    <span>Accept Connection</span>
                    <Icon name="check" className="text-xl" />
                  </button>
                  <button
                    onClick={handleDeclineFlow}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-text-secondary transition-all hover:border-gray-300 hover:bg-gray-50"
                  >
                    Decline
                  </button>
                </div>
              )}

              {/* ---- Phone form (shown after Accept, when phone is not pre-verified) ---- */}
              {pageState.kind === "phone_form" && (
                <div className="mt-6 w-full">
                  <p className="mb-3 text-sm text-text-secondary">
                    To accept this connection, we need to verify your identity. Enter your phone number to get started.
                  </p>
                  <form onSubmit={handlePhoneSubmit} noValidate className="space-y-2">
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Icon name="phone" className="text-lg text-text-muted" />
                      </div>
                      <input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="Phone number"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (phoneError) setPhoneError(null);
                          if (apiError) setApiError(null);
                        }}
                        aria-label="Your phone number"
                        aria-invalid={!!phoneError}
                        className={`h-11 w-full rounded-xl border py-2.5 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none ${phoneError ? "border-error" : "border-gray-300"}`}
                      />
                    </div>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Icon name="badge" className="text-lg text-text-muted" />
                      </div>
                      <input
                        type="text"
                        autoComplete="family-name"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        aria-label="Your last name"
                        className="h-11 w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                      />
                    </div>
                    {phoneError && (
                      <p className="mt-1 text-xs text-error" role="alert">{phoneError}</p>
                    )}
                    <button
                      type="submit"
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-orange-500/30 transition-all hover:from-orange-600 hover:to-orange-700"
                    >
                      Send Verification Link
                      <Icon name="arrow_forward" className="text-base" />
                    </button>
                  </form>
                  <button
                    onClick={() => setPageState({ kind: "show_requestor", data })}
                    className="mt-3 text-xs text-text-muted hover:text-text-secondary"
                  >
                    ← Back
                  </button>
                </div>
              )}

              {/* ---- Verify waiting (SMS sent) ---- */}
              {pageState.kind === "verify_waiting" && (
                <div className="mt-8 flex flex-col items-center text-center">
                  <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                    <Icon name="smartphone" className="text-2xl text-verified" />
                  </div>
                  <p className="text-sm font-semibold text-text-primary">Check Your Phone</p>
                  <p className="mt-1 text-xs text-text-secondary">
                    We sent a verification link to your phone. Click it to continue.
                  </p>
                  <button
                    onClick={() => {
                      setPageState({ kind: "show_requestor", data });
                      setApiError(null);
                    }}
                    className="mt-4 text-xs text-text-muted hover:text-text-secondary"
                  >
                    Use a different number
                  </button>
                </div>
              )}

              {/* ---- Verifying / actioning spinner ---- */}
              {isActioning && (
                <div className="mt-8 flex flex-col items-center">
                  <LogoSpinner
                    size="md"
                    label={pageState.kind === "verifying" ? "Verifying…" : "Processing…"}
                  />
                </div>
              )}

              {/* ---- What they're requesting (verification requirements) ---- */}
              {pageState.kind === "show_requestor" && data.required_fields && data.required_fields.length > 0 && (
                <div className="mt-5 w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    What they&apos;re requesting
                  </p>
                  <ul className="space-y-1.5">
                    {data.required_fields.map((field) => {
                      const cfg = REQUIRED_FIELD_CONFIGS[field];
                      if (!cfg) return null;
                      return (
                        <li key={field} className="flex items-center gap-2 text-xs text-amber-800">
                          <span
                            className="material-symbols-outlined select-none text-sm text-amber-600"
                            aria-hidden="true"
                          >
                            {cfg.icon}
                          </span>
                          <span>{cfg.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* ---- What you'll share (data sharing settings) ---- */}
              {pageState.kind === "show_requestor" && data.field_settings &&
                Object.values(data.field_settings).some((s) => s?.share === "share" || s?.share === "show_valid") && (
                <div className="mt-3 w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
                    What you&apos;ll share
                  </p>
                  <ul className="space-y-1.5">
                    {Object.entries(data.field_settings).map(([field, setting]) => {
                      if (!setting || (setting.share !== "share" && setting.share !== "show_valid")) return null;
                      const cfg = REQUIRED_FIELD_CONFIGS[field];
                      if (!cfg) return null;
                      return (
                        <li key={field} className="flex items-center gap-2 text-xs text-blue-800">
                          <span
                            className="material-symbols-outlined select-none text-sm text-blue-600"
                            aria-hidden="true"
                          >
                            {cfg.icon}
                          </span>
                          <span>{cfg.label}</span>
                          <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${setting.share === "show_valid" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                            <span className="material-symbols-outlined text-[11px]" aria-hidden="true">
                              {setting.share === "show_valid" ? "verified" : "share"}
                            </span>
                            {setting.share === "show_valid" ? "Verified badge only" : "Actual value"}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Mobile-only Accept/Decline — outside the card, authenticated users only.
            Unauthenticated users submit directly via the phone form inside the card. */}
        {pageState.kind === "show_requestor" && isAuthenticated && (
          <div className="mt-4 flex w-full max-w-5xl flex-col gap-3 md:hidden">
            <button
              onClick={handleAcceptFlow}
              className="flex w-full items-center justify-center gap-3 rounded-2xl bg-primary-500 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-600"
            >
              <span>Accept Connection</span>
              <Icon name="check" className="text-xl" />
            </button>
            <button
              onClick={handleDeclineFlow}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-text-secondary transition-all hover:border-gray-300 hover:bg-gray-50"
            >
              Decline
            </button>
          </div>
        )}
        {/* Mobile Decline for unauthenticated — phone form handles accept, just need a way out */}
        {pageState.kind === "show_requestor" && !isAuthenticated && (
          <div className="mt-3 md:hidden">
            <button
              onClick={handleDeclineFlow}
              className="text-sm font-medium text-text-muted hover:text-text-secondary"
            >
              Decline this request
            </button>
          </div>
        )}

        {/* Trust indicators */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-xs text-text-muted sm:gap-6">
          <div className="flex items-center gap-1.5">
            <Icon name="lock" className="text-sm text-verified" />
            <span>End-to-end encrypted</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="shield" className="text-sm text-verified" />
            <span>Zero-knowledge sharing</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="verified_user" className="text-sm text-verified" />
            <span>Identity never stored</span>
          </div>
        </div>

        {/* Info panel for unauthenticated users */}
        {!isAuthenticated && pageState.kind === "show_requestor" && (
          <div className="mt-8 w-full max-w-xl rounded-2xl border border-primary-100 bg-primary-50 p-5 text-center sm:p-6">
            <div className="flex items-start justify-center gap-3 sm:items-center">
              <Icon name="info" className="mt-0.5 shrink-0 text-xl text-primary-500 sm:mt-0" />
              <p className="text-left text-sm text-text-secondary">
                <strong className="text-text-primary">{firstName}</strong> has verified their
                identity on <BrandName /> and is requesting that you do the same. Your information
                will only be shared once both parties are verified.
              </p>
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-4">
              <Link
                to="/how-it-works"
                className="inline-flex items-center justify-center gap-1 text-sm font-medium text-primary-500 transition-colors hover:text-primary-600"
              >
                Learn how it works <Icon name="arrow_forward" className="text-base" />
              </Link>
              <Link
                to="/security"
                className="inline-flex items-center justify-center gap-1 text-sm font-medium text-primary-500 transition-colors hover:text-primary-600"
              >
                See our security <Icon name="arrow_forward" className="text-base" />
              </Link>
            </div>
          </div>
        )}
      </main>

      <PageFooter />
    </div>
  );
}
